<script setup>
import { onBeforeUnmount, onMounted, ref } from 'vue';

const isAnimating = ref(false);
const circlePosition = ref({ x: 0, y: 0 });
const additionalCircles = ref([]);
const animationId = ref(null);
const additionalAnimationIds = ref([]);
const fadeAnimationIds = ref([]);

const circleGradients = [
  'radial-gradient(circle, rgba(147, 51, 234, 0.2), rgba(147, 51, 234, 0.008) 60%, rgba(147, 51, 234, 0) 100%)', // Vibrant purple
  'radial-gradient(circle, rgba(79, 70, 229, 0.2), rgba(79, 70, 229, 0.008) 60%, rgba(79, 70, 229, 0) 100%)', // Deep indigo
  'radial-gradient(circle, rgba(236, 72, 153, 0.2), rgba(236, 72, 153, 0.008) 60%, rgba(236, 72, 153, 0) 100%)', // Magical pink
  'radial-gradient(circle, rgba(6, 182, 212, 0.2), rgba(6, 182, 212, 0.008) 60%, rgba(6, 182, 212, 0) 100%)', // Cyan blue
  'radial-gradient(circle, rgba(167, 139, 250, 0.2), rgba(167, 139, 250, 0.008) 60%, rgba(167, 139, 250, 0) 100%)', // Soft purple
  'radial-gradient(circle, rgba(45, 212, 191, 0.2), rgba(45, 212, 191, 0.008) 60%, rgba(45, 212, 191, 0) 100%)', // Teal
  'radial-gradient(circle, rgba(192, 132, 252, 0.2), rgba(192, 132, 252, 0.008) 60%, rgba(192, 132, 252, 0) 100%)', // Light purple
  'radial-gradient(circle, rgba(14, 165, 233, 0.2), rgba(14, 165, 233, 0.008) 60%, rgba(14, 165, 233, 0) 100%)', // Sky blue
  'radial-gradient(circle, rgba(217, 70, 239, 0.2), rgba(217, 70, 239, 0.008) 60%, rgba(217, 70, 239, 0) 100%)', // Fuchsia
];

function startAnimation() {
  isAnimating.value = true;
  const startX = window.innerWidth;
  const startY = window.innerHeight;
  const targetX = window.innerWidth / 2;
  const targetY = window.innerHeight / 2;

  circlePosition.value = { x: startX, y: startY };
  additionalCircles.value = [
    { x: startX, y: startY, targetX: window.innerWidth / 4, targetY: window.innerHeight / 4, gradient: circleGradients[1] },
    { x: startX, y: startY, targetX: window.innerWidth * 3 / 4, targetY: window.innerHeight / 4, gradient: circleGradients[2] },
    { x: startX, y: startY, targetX: window.innerWidth / 4, targetY: window.innerHeight * 3 / 4, gradient: circleGradients[3] },
    { x: startX, y: startY, targetX: window.innerWidth * 3 / 4, targetY: window.innerHeight * 3 / 4, gradient: circleGradients[4] },
    { x: startX, y: startY, targetX: window.innerWidth / 2, targetY: window.innerHeight / 3, gradient: circleGradients[5] },
    { x: startX, y: startY, targetX: window.innerWidth / 2, targetY: window.innerHeight * 2 / 3, gradient: circleGradients[6] },
    { x: startX, y: startY, targetX: window.innerWidth / 3, targetY: window.innerHeight / 2, gradient: circleGradients[7] },
    { x: startX, y: startY, targetX: window.innerWidth * 2 / 3, targetY: window.innerHeight / 2, gradient: circleGradients[8] },
    { x: startX, y: startY, targetX: window.innerWidth / 8, targetY: window.innerHeight / 2, gradient: circleGradients[0] },
    { x: startX, y: startY, targetX: window.innerWidth / 8, targetY: window.innerHeight / 4, gradient: circleGradients[1] },
    { x: startX, y: startY, targetX: window.innerWidth / 8, targetY: window.innerHeight * 3 / 4, gradient: circleGradients[2] },
    { x: startX, y: startY, targetX: window.innerWidth / 16, targetY: window.innerHeight / 2, gradient: circleGradients[3] },
    { x: startX, y: startY, targetX: window.innerWidth / 5, targetY: window.innerHeight / 5, gradient: circleGradients[4] },
    { x: startX, y: startY, targetX: window.innerWidth / 5, targetY: window.innerHeight * 4 / 5, gradient: circleGradients[5] },
    { x: startX, y: startY, targetX: window.innerWidth / 2, targetY: window.innerHeight, gradient: circleGradients[6] },
    { x: startX, y: startY, targetX: 0, targetY: 0, gradient: circleGradients[1] },
    { x: startX, y: startY, targetX: window.innerWidth * 0.35, targetY: window.innerHeight * 0.9, gradient: circleGradients[6] },
    { x: startX, y: startY, targetX: window.innerWidth * 0.6, targetY: window.innerHeight * 0.8, gradient: circleGradients[6] },
    { x: startX, y: startY, targetX: window.innerWidth * 0.7, targetY: window.innerHeight * 0.9, gradient: circleGradients[6] },
    { x: startX, y: startY, targetX: window.innerWidth * 0.4, targetY: window.innerHeight * 0.9, gradient: circleGradients[6] },
    { x: startX, y: startY, targetX: window.innerWidth * 0.05, targetY: window.innerHeight * 0.85, gradient: circleGradients[0] },
    { x: startX, y: startY, targetX: window.innerWidth * 0.35, targetY: window.innerHeight * 0.7, gradient: circleGradients[6] },
    { x: startX, y: startY, targetX: window.innerWidth * 0.25, targetY: window.innerHeight * 0.5, gradient: circleGradients[6] },
    { x: startX, y: startY, targetX: window.innerWidth * 0.55, targetY: window.innerHeight * 0.15, gradient: circleGradients[3] },
    { x: startX, y: startY, targetX: window.innerWidth * 0.7, targetY: window.innerHeight * 0.15, gradient: circleGradients[4] },
    { x: startX, y: startY, targetX: window.innerWidth * 0.85, targetY: window.innerHeight * 0.15, gradient: circleGradients[1] },
    { x: startX, y: startY, targetX: window.innerWidth * 0.35, targetY: window.innerHeight * 0.15, gradient: circleGradients[2] },
    { x: startX, y: startY, targetX: window.innerWidth * 0.45, targetY: window.innerHeight * 0.15, gradient: circleGradients[3] },
    { x: startX, y: startY, targetX: window.innerWidth * 0.25, targetY: window.innerHeight * 0.95, gradient: circleGradients[6] },
    { x: startX, y: startY, targetX: window.innerWidth * 0.6, targetY: window.innerHeight * 0.5, gradient: circleGradients[5] },
    { x: startX, y: startY, targetX: window.innerWidth * 0.4, targetY: window.innerHeight * 0.5, gradient: circleGradients[4] },
    { x: startX, y: startY, targetX: window.innerWidth * 0.1, targetY: window.innerHeight * 0.1, gradient: circleGradients[5] },
    { x: startX, y: startY, targetX: window.innerWidth * 0.3, targetY: window.innerHeight * 0.1, gradient: circleGradients[5] },
    { x: startX, y: startY, targetX: window.innerWidth * 0.6, targetY: window.innerHeight * 0.1, gradient: circleGradients[5] },
    { x: startX, y: startY, targetX: window.innerWidth * 0.55, targetY: window.innerHeight * 0.65, gradient: circleGradients[5] },
  ];

  const animate = () => {
    circlePosition.value.x -= (circlePosition.value.x - targetX) * 0.08;
    circlePosition.value.y -= (circlePosition.value.y - targetY) * 0.08;

    if (Math.abs(circlePosition.value.x - targetX) > 1
      || Math.abs(circlePosition.value.y - targetY) > 1) {
      animationId.value = requestAnimationFrame(animate);
    }
    else {
      isAnimating.value = false;
      circlePosition.value.x = targetX;
      circlePosition.value.y = targetY;
    }
  };

  animationId.value = requestAnimationFrame(animate);

  additionalCircles.value.forEach((circle, index) => {
    const animateAdditional = () => {
      circle.x -= (circle.x - circle.targetX) * 0.08;
      circle.y -= (circle.y - circle.targetY) * 0.08;

      if (Math.abs(circle.x - circle.targetX) > 1 || Math.abs(circle.y - circle.targetY) > 1) {
        additionalAnimationIds.value[index] = requestAnimationFrame(animateAdditional);
      }
      else {
        circle.x = circle.targetX;
        circle.y = circle.targetY;
      }
    };

    additionalAnimationIds.value[index] = requestAnimationFrame(animateAdditional);
  });
}

onMounted(() => {
  startAnimation();
});

onBeforeUnmount(() => {
  if (animationId.value) {
    cancelAnimationFrame(animationId.value);
  }
  additionalAnimationIds.value.forEach((id) => {
    if (id)
      cancelAnimationFrame(id);
  });
  fadeAnimationIds.value.forEach((id) => {
    if (id)
      cancelAnimationFrame(id);
  });
});
</script>

<template>
  <!-- Not used currently as actions are disabled by the backend -->
  <div class="magic-effect-container">
    <div
      class="magic-circle"
      :style="{
        left: `${circlePosition.x}px`,
        top: `${circlePosition.y}px`,
        background: circleGradients[0],
        opacity: 1,
        width: `${circlePosition.width || 600}px`,
        height: `${circlePosition.height || 600}px`,
      }"
    />
    <div
      v-for="(circle, index) in additionalCircles"
      :key="index"
      class="magic-circle"
      :style="{
        left: `${circle.x}px`,
        top: `${circle.y}px`,
        background: circle.gradient,
        opacity: 1,
        width: `${circle.width || 600}px`,
        height: `${circle.height || 600}px`,
      }"
    />
  </div>
</template>

<style scoped>
.magic-effect-container {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}

.magic-circle {
  position: absolute;
  border-radius: 50%;
  transform: translate(-50%, -50%);
  filter: blur(15px);
  will-change: transform, opacity;
}
</style>
